/**
 * Chart of accounts templates (plan §4.4). One template for the Company book and one, smaller,
 * for every franchise book (D4, O4). The catalog is synced into `chart_template_accounts` on every
 * deploy; the database then adds any missing template account to every book (never renames or
 * removes an account the owner has edited). Cash-box accounts are not in the template: one child
 * of 1100 is created automatically for each cash box.
 *
 * - Header accounts (`postable: false`) only group accounts for reports.
 * - `systemKey` names the accounts the code posts to (the ledger service looks them up by key).
 */
export type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE';
export type BookKind = 'COMPANY' | 'FRANCHISE';

export interface ChartAccount {
  readonly code: string;
  readonly parent: string | null;
  readonly type: AccountType;
  readonly postable: boolean;
  readonly nameAr: string;
  readonly nameEn: string;
  readonly systemKey?: string;
}

const header = (
  code: string,
  parent: string | null,
  type: AccountType,
  nameAr: string,
  nameEn: string,
  systemKey?: string,
): ChartAccount => ({
  code,
  parent,
  type,
  postable: false,
  nameAr,
  nameEn,
  ...(systemKey ? { systemKey } : {}),
});
const leaf = (
  code: string,
  parent: string,
  type: AccountType,
  nameAr: string,
  nameEn: string,
  systemKey?: string,
): ChartAccount => ({
  code,
  parent,
  type,
  postable: true,
  nameAr,
  nameEn,
  ...(systemKey ? { systemKey } : {}),
});

/** Accounts every book has (cash, wallets, sales per channel, cost of sales, running costs). */
const shared = {
  assets: header('1000', null, 'ASSET', 'الأصول', 'Assets'),
  cash: header('1100', '1000', 'ASSET', 'النقد (الصناديق)', 'Cash (cash boxes)', 'cash_boxes'),
  inTransit: leaf('1150', '1000', 'ASSET', 'نقد قيد التحويل', 'Cash in transit', 'cash_in_transit'),
  differences: leaf(
    '1160',
    '1000',
    'ASSET',
    'فروقات نقدية قيد المراجعة',
    'Cash differences under review',
    'cash_differences',
  ),
  wallets: header('1200', '1000', 'ASSET', 'البطاقات والمحافظ الإلكترونية', 'Card and wallets'),
  card: leaf('1210', '1200', 'ASSET', 'البطاقة المصرفية', 'Card', 'wallet_card'),
  zainCash: leaf('1220', '1200', 'ASSET', 'زين كاش', 'ZainCash', 'wallet_zaincash'),
  qi: leaf('1230', '1200', 'ASSET', 'كي كارد', 'Qi', 'wallet_qi'),
  receivables: header('1300', '1000', 'ASSET', 'الذمم المدينة', 'Receivables'),
  // One child per platform is added in Phase 3, once the owner names them (plan question 14).
  platforms: header(
    '1310',
    '1300',
    'ASSET',
    'مستحقات منصات التوصيل',
    'Delivery platforms receivable',
    'platform_receivables',
  ),
  inventory: header('1400', '1000', 'ASSET', 'المخزون', 'Inventory'),
  liabilities: header('2000', null, 'LIABILITY', 'الالتزامات', 'Liabilities'),
  suppliers: leaf(
    '2100',
    '2000',
    'LIABILITY',
    'ذمم الموردين',
    'Suppliers payable',
    'suppliers_payable',
  ),
  salaries: leaf(
    '2200',
    '2000',
    'LIABILITY',
    'رواتب مستحقة',
    'Salaries payable',
    'salaries_payable',
  ),
  equity: header('3000', null, 'EQUITY', 'حقوق الملكية', 'Equity'),
  retained: leaf(
    '3300',
    '3000',
    'EQUITY',
    'الأرباح المحتجزة',
    'Retained earnings',
    'retained_earnings',
  ),
  opening: leaf('3900', '3000', 'EQUITY', 'أرصدة افتتاحية', 'Opening balances', 'opening_balances'),
  revenue: header('4000', null, 'REVENUE', 'الإيرادات', 'Revenue'),
  sales: header('4100', '4000', 'REVENUE', 'المبيعات حسب القناة', 'Sales by channel'),
  dineIn: leaf('4110', '4100', 'REVENUE', 'مبيعات الصالة', 'Dine-in sales', 'sales_dine_in'),
  takeaway: leaf('4120', '4100', 'REVENUE', 'مبيعات السفري', 'Takeaway sales', 'sales_takeaway'),
  ownDelivery: leaf(
    '4130',
    '4100',
    'REVENUE',
    'مبيعات التوصيل الخاص',
    'Own delivery sales',
    'sales_own_delivery',
  ),
  callCenter: leaf(
    '4140',
    '4100',
    'REVENUE',
    'مبيعات مركز الاتصال',
    'Call center sales',
    'sales_call_center',
  ),
  online: leaf(
    '4150',
    '4100',
    'REVENUE',
    'مبيعات التطبيق والموقع',
    'App and online sales',
    'sales_online',
  ),
  kiosk: leaf('4160', '4100', 'REVENUE', 'مبيعات الكشك الذاتي', 'Kiosk sales', 'sales_kiosk'),
  // One child per platform in Phase 3 (plan question 14).
  platformSales: header(
    '4170',
    '4100',
    'REVENUE',
    'مبيعات منصات التوصيل',
    'Delivery platform sales',
    'sales_platforms',
  ),
  otherIncome: leaf('4900', '4000', 'REVENUE', 'إيرادات أخرى', 'Other income', 'other_income'),
  costOfSales: header('5000', null, 'EXPENSE', 'تكلفة المبيعات', 'Cost of sales'),
  food: leaf('5100', '5000', 'EXPENSE', 'تكلفة المواد الغذائية', 'Food cost', 'food_cost'),
  packaging: leaf('5200', '5000', 'EXPENSE', 'التغليف', 'Packaging', 'packaging_cost'),
  waste: leaf('5300', '5000', 'EXPENSE', 'الهدر والتالف', 'Waste', 'waste'),
  // Phase 2 (plan §4.1, W3): both books count stock and value it at weighted average cost.
  countDifferences: leaf(
    '5400',
    '5000',
    'EXPENSE',
    'فروقات الجرد',
    'Stock count differences',
    'stock_count_differences',
  ),
  costAdjustments: leaf(
    '5500',
    '5000',
    'EXPENSE',
    'تسويات التكلفة',
    'Cost adjustments',
    'cost_adjustments',
  ),
  expenses: header('6000', null, 'EXPENSE', 'المصاريف التشغيلية', 'Operating expenses'),
  wages: leaf('6100', '6000', 'EXPENSE', 'الرواتب والأجور', 'Salaries and wages', 'salaries'),
  rent: leaf('6200', '6000', 'EXPENSE', 'الإيجار', 'Rent', 'rent'),
  utilities: header('6300', '6000', 'EXPENSE', 'الخدمات', 'Utilities'),
  electricity: leaf('6310', '6300', 'EXPENSE', 'الكهرباء', 'Electricity', 'electricity'),
  generator: leaf(
    '6320',
    '6300',
    'EXPENSE',
    'اشتراك المولدة',
    'Generator subscription',
    'generator',
  ),
  water: leaf('6330', '6300', 'EXPENSE', 'الماء', 'Water', 'water'),
  internet: leaf('6340', '6300', 'EXPENSE', 'الإنترنت', 'Internet', 'internet'),
  maintenance: leaf('6400', '6000', 'EXPENSE', 'الصيانة', 'Maintenance', 'maintenance'),
  marketing: leaf('6500', '6000', 'EXPENSE', 'التسويق', 'Marketing', 'marketing'),
  commissions: leaf(
    '6600',
    '6000',
    'EXPENSE',
    'عمولات منصات التوصيل',
    'Aggregator commissions',
    'aggregator_commissions',
  ),
  cleaning: leaf(
    '6700',
    '6000',
    'EXPENSE',
    'التنظيف والمستلزمات',
    'Cleaning and supplies',
    'cleaning',
  ),
  government: leaf(
    '6800',
    '6000',
    'EXPENSE',
    'الرسوم الحكومية والتراخيص',
    'Government fees and licenses',
    'government_fees',
  ),
  other: leaf('6900', '6000', 'EXPENSE', 'مصاريف أخرى', 'Other expenses', 'other_expenses'),
} as const;

/**
 * The four partners of the company, 25% each (owner, 2026-09-25). Arabic is the official
 * spelling. Their user accounts are linked to these equity accounts in Phase 3.
 */
export const PARTNERS = [
  { no: 1, nameAr: 'مالك انس عبدالله', nameEn: 'Malik Anas Abdullah', shareBp: 2_500 },
  { no: 2, nameAr: 'ايوب صادق كريم', nameEn: 'Ayoub Sadiq Karim', shareBp: 2_500 },
  { no: 3, nameAr: 'عمر حسن فاضل', nameEn: 'Omar Hasan Fadhil', shareBp: 2_500 },
  { no: 4, nameAr: 'محمد حسن فاضل', nameEn: 'Mohammed Hasan Fadhil', shareBp: 2_500 },
] as const;

const partnerAccounts = (base: '3100' | '3200', ar: string, en: string, key: string) =>
  PARTNERS.map((p) =>
    leaf(
      `${base.slice(0, 2)}${String(p.no)}0`,
      base,
      'EQUITY',
      `${ar} - ${p.nameAr}`,
      `${en} - ${p.nameEn}`,
      `${key}_${String(p.no)}`,
    ),
  );

export const COMPANY_CHART: readonly ChartAccount[] = [
  shared.assets,
  shared.cash,
  shared.inTransit,
  shared.differences,
  shared.wallets,
  shared.card,
  shared.zainCash,
  shared.qi,
  shared.receivables,
  shared.platforms,
  leaf(
    '1320',
    '1300',
    'ASSET',
    'عوائد الفرنشايز المستحقة',
    'Franchise royalties receivable',
    'royalty_receivable',
  ),
  leaf(
    '1330',
    '1300',
    'ASSET',
    'ذمم بين المواقع',
    'Inter-location receivable',
    'inter_location_receivable',
  ),
  leaf('1340', '1300', 'ASSET', 'سلف الموظفين', 'Employee advances', 'employee_advances'),
  // Phase 2 (plan §4.1, W3). Party accounts: each line names its franchise or supplier.
  leaf(
    '1350',
    '1300',
    'ASSET',
    'ذمم الفرنشايز عن البضاعة',
    'Franchise goods receivable',
    'franchise_goods_receivable',
  ),
  leaf(
    '1360',
    '1300',
    'ASSET',
    'مطالبات على الموردين',
    'Supplier claims and refunds due',
    'supplier_claims',
  ),
  shared.inventory,
  leaf(
    '1410',
    '1400',
    'ASSET',
    'مخزون المخزن المركزي',
    'Warehouse inventory',
    'inventory_warehouse',
  ),
  leaf('1420', '1400', 'ASSET', 'مخزون الفروع', 'Branch inventory', 'inventory_branches'),
  leaf('1430', '1400', 'ASSET', 'مخزون قيد النقل', 'Inventory in transit', 'inventory_in_transit'),
  leaf(
    '1440',
    '1400',
    'ASSET',
    'فروقات مخزون قيد المراجعة',
    'Stock differences under review',
    'stock_differences',
  ),
  leaf('1500', '1000', 'ASSET', 'المعدات والأجهزة', 'Equipment', 'equipment'),
  shared.liabilities,
  shared.suppliers,
  shared.salaries,
  leaf(
    '2400',
    '2000',
    'LIABILITY',
    'ذمم دائنة بين المواقع',
    'Inter-location payable',
    'inter_location_payable',
  ),
  shared.equity,
  header('3100', '3000', 'EQUITY', 'رأس مال الشركاء', 'Partner capital'),
  ...partnerAccounts('3100', 'رأس المال', 'Capital', 'partner_capital'),
  header('3200', '3000', 'EQUITY', 'مسحوبات الشركاء', 'Partner drawings'),
  ...partnerAccounts('3200', 'المسحوبات', 'Drawings', 'partner_withdrawals'),
  shared.retained,
  shared.opening,
  shared.revenue,
  shared.sales,
  shared.dineIn,
  shared.takeaway,
  shared.ownDelivery,
  shared.callCenter,
  shared.online,
  shared.kiosk,
  shared.platformSales,
  leaf(
    '4200',
    '4000',
    'REVENUE',
    'مبيعات البضاعة للفرنشايز',
    'Goods sales to franchises',
    'franchise_goods_sales',
  ),
  leaf(
    '4500',
    '4000',
    'REVENUE',
    'إيرادات عوائد الفرنشايز',
    'Franchise royalty income',
    'royalty_income',
  ),
  shared.otherIncome,
  shared.costOfSales,
  shared.food,
  shared.packaging,
  shared.waste,
  shared.countDifferences,
  shared.costAdjustments,
  leaf(
    '5600',
    '5000',
    'EXPENSE',
    'تكلفة البضاعة المباعة للفرنشايز',
    'Cost of goods sold to franchises',
    'franchise_goods_cost',
  ),
  leaf(
    '5700',
    '5000',
    'EXPENSE',
    'تكاليف إنتاج محمَّلة على المخزون',
    'Production costs absorbed into stock',
    'production_absorbed',
  ),
  shared.expenses,
  shared.wages,
  shared.rent,
  shared.utilities,
  shared.electricity,
  shared.generator,
  shared.water,
  shared.internet,
  shared.maintenance,
  shared.marketing,
  shared.commissions,
  shared.cleaning,
  shared.government,
  shared.other,
];

/** A franchise runs its own books; the company only records the royalty (D4). */
export const FRANCHISE_CHART: readonly ChartAccount[] = [
  shared.assets,
  shared.cash,
  shared.inTransit,
  shared.differences,
  shared.wallets,
  shared.card,
  shared.zainCash,
  shared.qi,
  shared.receivables,
  shared.platforms,
  shared.inventory,
  leaf('1420', '1400', 'ASSET', 'مخزون الفرع', 'Branch inventory', 'inventory_branches'),
  shared.liabilities,
  shared.suppliers,
  shared.salaries,
  leaf(
    '2300',
    '2000',
    'LIABILITY',
    'عوائد مستحقة للشركة الأم',
    'Royalty payable to Dob Burger',
    'royalty_payable',
  ),
  // Phase 2 (plan §4.1): goods a live franchise received from the warehouse and has not paid.
  leaf(
    '2310',
    '2000',
    'LIABILITY',
    'ذمم البضاعة للشركة الأم',
    'Payable to Dob Burger for goods',
    'company_goods_payable',
  ),
  shared.equity,
  leaf('3100', '3000', 'EQUITY', 'حقوق المالك', "Owner's equity", 'owner_equity'),
  shared.retained,
  shared.opening,
  shared.revenue,
  shared.sales,
  shared.dineIn,
  shared.takeaway,
  shared.ownDelivery,
  shared.callCenter,
  shared.online,
  shared.kiosk,
  shared.platformSales,
  shared.otherIncome,
  shared.costOfSales,
  shared.food,
  shared.packaging,
  shared.waste,
  shared.countDifferences,
  shared.costAdjustments,
  shared.expenses,
  shared.wages,
  shared.rent,
  shared.utilities,
  shared.electricity,
  shared.generator,
  shared.water,
  shared.internet,
  shared.maintenance,
  shared.marketing,
  leaf(
    '6550',
    '6000',
    'EXPENSE',
    'عوائد الفرنشايز',
    'Franchise royalty expense',
    'royalty_expense',
  ),
  shared.commissions,
  shared.cleaning,
  shared.government,
  shared.other,
];

/**
 * Accounts only their documents post to (Phase 2, W3): stock is matched to its movements and party
 * accounts name a party on every line, so a hand-made entry here would be refused by the database
 * at COMMIT. The ledger's own correction screens refuse them up front with a clear reason.
 */
export const DOCUMENT_KEPT_ACCOUNT_KEYS: readonly string[] = [
  'inventory_warehouse',
  'inventory_branches',
  'inventory_in_transit',
  'stock_differences',
  'suppliers_payable',
  'supplier_claims',
  'franchise_goods_receivable',
  'company_goods_payable',
];

export const CHARTS: Readonly<Record<BookKind, readonly ChartAccount[]>> = {
  COMPANY: COMPANY_CHART,
  FRANCHISE: FRANCHISE_CHART,
};

/** Debit-normal accounts grow with debits; the others with credits. */
export function isDebitNormal(type: AccountType): boolean {
  return type === 'ASSET' || type === 'EXPENSE';
}
